import QRCode from 'qrcode';
import type { QRCodeConfig } from '../types';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function drawRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Render a QR code onto a canvas element with custom styling.
 */
export async function renderQRCode(
  canvas: HTMLCanvasElement,
  config: QRCodeConfig,
): Promise<void> {
  const {
    text,
    foregroundColor,
    backgroundColor,
    errorCorrectionLevel,
    margin,
    dotStyle,
    logoDataUrl,
    logoSize,
  } = config;

  // Generate QR code bit matrix
  const qr = QRCode.create(text, { errorCorrectionLevel });
  const modules = qr.modules;
  const matrixSize = modules.size;

  const totalModules = matrixSize + 2 * margin;
  const moduleSize = canvas.width / totalModules;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw each module
  ctx.fillStyle = foregroundColor;

  for (let row = 0; row < matrixSize; row++) {
    for (let col = 0; col < matrixSize; col++) {
      const isDark = modules.get(row, col) !== 0;
      if (!isDark) continue;

      const x = (col + margin) * moduleSize;
      const y = (row + margin) * moduleSize;
      const size = moduleSize;

      switch (dotStyle) {
        case 'square': {
          ctx.fillRect(x, y, size, size);
          break;
        }
        case 'rounded': {
          const radius = size * 0.35;
          drawRoundRect(ctx, x, y, size, size, radius);
          ctx.fill();
          break;
        }
        case 'dot': {
          ctx.beginPath();
          ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
      }
    }
  }

  // Draw logo if provided
  if (logoDataUrl) {
    const logoMax = canvas.width * (Math.min(Math.max(logoSize, 10), 50) / 100);
    const logoX = (canvas.width - logoMax) / 2;
    const logoY = (canvas.height - logoMax) / 2;
    const padding = Math.max(4, moduleSize);

    try {
      const img = await loadImage(logoDataUrl);

      // White background behind logo
      ctx.fillStyle = backgroundColor;
      const bgX = logoX - padding;
      const bgY = logoY - padding;
      const bgSize = logoMax + padding * 2;
      drawRoundRect(ctx, bgX, bgY, bgSize, bgSize, padding);
      ctx.fill();

      ctx.drawImage(img, logoX, logoY, logoMax, logoMax);
    } catch {
      // Silently skip logo if it fails to load
    }
  }
}

/**
 * Download the QR code canvas as a PNG file.
 */
export function downloadCanvasAsPNG(canvas: HTMLCanvasElement, filename = 'qrcode.png'): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/png');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
